import { useInkind } from '@gik/api/inkinds/inkind';
import { withTitle } from '@gik/core/hocs/withTitle';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import { ErrorPageSection } from '@gik/core/pages/Error/ErrorPageSection';
import routes from '@gik/core/routes';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { navigateTo } from '@gik/core/utils/navigateTo';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { Button } from '@gik/ui-legacy/Button';
import { LayoutAside } from '@gik/ui-legacy/LayoutAside';
import { SvgIcon } from '@gik/ui-legacy/SvgIcon/SvgIcon';
import { PageHeader } from '@gik/ui-legacy/gik/PageHeader';
import { PageSection } from '@gik/ui-legacy/gik/PageSection';
import { RelatedArticlesSection } from '@gik/ui-legacy/gik/RelatedArticles';
import ChevronLeftIcon from '@heroicons/react/outline/ChevronLeftIcon';
import { useTranslation } from 'react-i18next';
import { WishlistGrid } from './WishlistGrid';
import { WishlistPageHeader } from './WishlistPageHeader';
import { translationKeys } from './i18n/en';

export type WishlistPageProps = {
  inkindRouteId: string;
} & UIComponent;

function generateTitle(title?: string) {
  return title ? `${title}'s Wishlist` : undefined;
}

function WishlistPageComp({ inkindRouteId }: WishlistPageProps) {
  const bem = useBemCN('wishlist-page');
  const isMdDown = useBreakpoint(Breakpoint.MD_DOWN);
  const { t } = useTranslation();

  const { data: inkindPage } = useInkind(inkindRouteId);

  function handleBack() {
    navigateTo(`${routes.inkinds}/${inkindRouteId}/${inkindPage?.slug}`);
  }

  return withTitle(
    {
      title: generateTitle(inkindPage?.title),
    },
    <div {...bem()}>
      <PageHeader variant="aqua">
        <WishlistPageHeader inkindRouteId={inkindRouteId} />

        <Button
          variant="primary-inverted"
          pill
          size="sm"
          prepend={<SvgIcon Icon={ChevronLeftIcon} size="sm" />}
          onClick={handleBack}
        >
          {t(translationKeys.backToPage)}
        </Button>
      </PageHeader>

      <PageSection>
        <LayoutAside asideWidth="310px" gutter="2rem" {...bem('layout')}>
          <main>
            <WishlistGrid
              inkindRouteId={inkindRouteId}
              // send user to gift picker page
              isFullWishlistPage={false}
              cols={isMdDown ? 2 : 4}
            />
          </main>
          {inkindPage?.enableSuggestedArticles && (
            <aside>
              <RelatedArticlesSection enabled={inkindPage?.enableSuggestedArticles} inkindRouteId={inkindRouteId} />
            </aside>
          )}
        </LayoutAside>
      </PageSection>
    </div>
  );
}

export const WishlistPage = withComponentErrorBoundary(WishlistPageComp, {
  ErrorFallback: ErrorPageSection,
});
